import React, { useState, useEffect, useRef } from "react";
import { Button } from "@risk-first/ui-core";
import { ForceByFilterOutcome, ForceByFilterResponse, ForceMode } from "../../store/types";
import {
  Overlay,
  Dialog,
  Title,
  Badge,
  Crumb,
  Stats,
  Stat,
  Warning,
  Message,
  Actions,
} from "./ForceGroupDialog.style";

export interface ForceGroupDialogProps {
  isOpen: boolean;
  mode: ForceMode;
  /** "Field=value" crumbs from the outermost group to the clicked one. */
  groupPath: string[];
  /** Runs the dry run; called once each time the dialog opens. */
  loadPreview: () => Promise<ForceByFilterOutcome>;
  onConfirm: () => void;
  onClose: () => void;
}

const levelLabel = (mode: ForceMode): string => (mode === "Client" ? "Client" : "Client Agent");

/**
 * Confirm popup for group-level Force (PMDEV-3736). Shows the dry-run counts and the level;
 * Confirm runs the same request without dryRun. Confirm is disabled while loading, when there is
 * nothing to force, or when the dry run failed (e.g. over the key cap).
 */
export const ForceGroupDialog = ({
  isOpen,
  mode,
  groupPath,
  loadPreview,
  onConfirm,
  onClose,
}: ForceGroupDialogProps): JSX.Element | null => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<ForceByFilterResponse | undefined>();
  const [error, setError] = useState<string | undefined>();
  const dialogRef = useRef<HTMLDivElement>(null);
  const loadPreviewRef = useRef(loadPreview);
  loadPreviewRef.current = loadPreview;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let active = true;
    setLoading(true);
    setPreview(undefined);
    setError(undefined);

    loadPreviewRef.current().then((outcome) => {
      if (!active) {
        return;
      }

      setPreview(outcome.data);
      setError(outcome.error);
      setLoading(false);
    });

    return () => {
      active = false;
      // Clear on close so a reopen never renders the previous group's counts with Confirm enabled.
      setLoading(true);
      setPreview(undefined);
      setError(undefined);
    };
  }, [isOpen]);

  // When open: move focus into the dialog and close on Escape (keyboard accessibility).
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    dialogRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const level = levelLabel(mode);
  const canConfirm = !loading && !error && (preview?.count ?? 0) > 0;

  return (
    <Overlay data-testid="force-group-dialog-overlay">
      <Dialog ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Force dependencies for group">
        <Title>
          Force dependencies for group <Badge data-testid="force-group-level">{level} level</Badge>
        </Title>
        <Crumb data-testid="force-group-path">{groupPath.join(" › ")}</Crumb>

        {loading && <Message data-testid="force-group-loading">Counting dependencies…</Message>}

        {error && <Warning data-testid="force-group-error">{error}</Warning>}

        {!loading && !error && preview && (
          <>
            <Stats>
              <Stat>
                <b data-testid="force-group-rows">{preview.rows}</b>
                <small>dependencies to force</small>
              </Stat>
              <Stat>
                <b data-testid="force-group-targets">{preview.count}</b>
                <small>distinct key × {level.toLowerCase()}</small>
              </Stat>
              <Stat>
                <b data-testid="force-group-skipped">{preview.skippedMissingTarget}</b>
                <small>skipped (no {level})</small>
              </Stat>
            </Stats>
            {preview.count === 0 ? (
              <Message data-testid="force-group-nothing">Nothing to force in this group.</Message>
            ) : (
              <Warning>
                Each dependency will be marked Completed. Data will <strong>NOT</strong> be verified.
              </Warning>
            )}
          </>
        )}

        <Actions>
          <Button size="small" variant="secondary" data-testid="force-group-cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="primary"
            data-testid="force-group-confirm"
            disabled={!canConfirm}
            onClick={() => {
              if (canConfirm) {
                onConfirm();
              }
            }}
          >
            Confirm
          </Button>
        </Actions>
      </Dialog>
    </Overlay>
  );
};
